import { Models } from "appwrite";
import { Link, useNavigate } from "react-router-dom";
import { PostStats } from "@/components/shared";
import { multiFormatDateString } from "@/lib/utils";
import { useUserContext } from "@/context/AuthContext";
import { Ban, BadgeCheck, ChevronLeft, ChevronRight, Flag, Volume2, VolumeX } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useGetUserById } from "@/lib/react-query/queries";
import { isValidMediaUrl } from "@/lib/appwrite/config";
import { buildSignInRedirect } from "@/lib/auth/redirect";
import SellerRatingBadge from "@/components/shared/SellerRatingBadge";
import CreatorAvatar from "@/components/shared/CreatorAvatar";
import ReportDialog from "@/components/shared/ReportDialog";
import { toast } from "@/components/ui/use-toast";
import { blockUser } from "@/lib/safety";


type PostCardProps = {
  post: Models.Document;
  showDealBadge?: boolean;
};

// Global video manager - ensures only one video plays at a time
class GlobalVideoManager {
  private static instance: GlobalVideoManager;
  private currentVideo: HTMLVideoElement | null = null;
  private currentPostId: string | null = null;

  static getInstance(): GlobalVideoManager {
    if (!GlobalVideoManager.instance) {
      GlobalVideoManager.instance = new GlobalVideoManager();
    }
    return GlobalVideoManager.instance;
  }

  playVideo(video: HTMLVideoElement, postId: string): void {
    // Pause any currently playing video
    if (this.currentVideo && this.currentPostId !== postId) {
      this.currentVideo.pause();
    }
    
    this.currentVideo = video;
    this.currentPostId = postId;
    
    // Attempt to play the new video
    video.play().catch(err => {
      console.log(`Video play failed for post ${postId}:`, err);
    });
  }

  pauseVideo(postId: string): void {
    if (this.currentPostId === postId && this.currentVideo) {
      this.currentVideo.pause();
      this.currentVideo = null;
      this.currentPostId = null;
    }
  }

  pauseAllVideos(): void {
    if (this.currentVideo) {
      this.currentVideo.pause();
      this.currentVideo = null;
      this.currentPostId = null;
    }
  }

  isVideoPlaying(postId: string): boolean {
    return this.currentPostId === postId;
  }
}

const DealTypeBadge = ({ dealType }: { dealType: string }) => {
  const dealStyles = {
    'physical-goods': 'bg-green-300 text-blue-800',
    'services': 'bg-purple-100 text-purple-800',
    'digital': 'bg-green-100 text-green-800'
  }[dealType] || 'bg-gray-100 text-gray-800';

  const dealLabels = {
    'physical-goods': 'Physical Goods',
    'services': 'Services',
    'digital': 'Digital'
  }[dealType] || dealType;

  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 whitespace-nowrap ${dealStyles}`}>
      {dealLabels}
    </span>
  );
};

const PostCard = ({ post, showDealBadge = false }: PostCardProps) => {
  // ALL HOOKS MUST BE CALLED FIRST
  const { user, isAuthenticated } = useUserContext();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const postRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [creatorBlocked, setCreatorBlocked] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);

  
  const { data: creator, isLoading: isCreatorLoading } = useGetUserById(post.creatorId || "");

  // Get global video manager
  const videoManager = GlobalVideoManager.getInstance();

  // Keep each URL paired with its type and API sort order. Invalid legacy rows
  // must not create a broken image placeholder in the feed.
  const postMedia = Array.isArray(post.media)
    ? post.media
        .filter((item: any) => isValidMediaUrl(item?.url))
        .sort((left: any, right: any) => (left?.sortOrder ?? 0) - (right?.sortOrder ?? 0))
    : [];
  const mediaFiles = postMedia.length > 0
    ? postMedia.map((item: any) => item.url)
    : (typeof post.imageUrl === 'string'
        ? (isValidMediaUrl(post.imageUrl) ? [post.imageUrl.trim()] : [])
        : Array.isArray(post.imageUrl) ? post.imageUrl.filter(isValidMediaUrl).map((url) => url.trim()) : []);
  const mediaTypes = postMedia.length > 0
    ? postMedia.map((item: any) => item.mediaType)
    : Array.isArray(post.mediaType) ? post.mediaType : [];
  const creatorName = creator?.name || (post as any).creatorName || "Busiconn member";
  const creatorImageUrl = isValidMediaUrl(creator?.imageUrl)
    ? creator.imageUrl
    : isValidMediaUrl((post as any).creatorImageUrl)
      ? (post as any).creatorImageUrl.trim()
      : "/assets/icons/profile-placeholder.svg";
  const likesCount = Array.isArray((post as any).likes)
    ? (post as any).likes.length
    : Number((post as any).likesCount ?? (post as any).likes ?? 0) || 0;
  const redirectGuestToSignIn = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(buildSignInRedirect(`${window.location.pathname}${window.location.search}`));
  };

  const handleBlockCreator = async () => {
    if (creatorBlocked || isBlocking) return;
    if (!window.confirm("Block this user? New contact from this user will be blocked.")) return;

    setIsBlocking(true);
    try {
      await blockUser(post.creatorId);
      setCreatorBlocked(true);
      toast({ title: "User blocked", description: "New contact from this user is now blocked." });
    } catch (error) {
      toast({
        title: "Unable to block user",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsBlocking(false);
    }
  };

  // Backend media URLs do not include file extensions, so prefer mediaType when available.
  const isVideo = (url: string, index = 0) => {
    return mediaTypes[index]?.toLowerCase?.().startsWith('video/') ||
           mediaTypes[index]?.toLowerCase?.() === 'video' ||
           url?.includes('&type=video') || url?.includes('/view?') || 
           url?.match(/\.(mp4|webm|ogg|mov)$/i);
  };

  // Check if current media is video
  const isCurrentMediaVideo = mediaFiles.length > 0 && isVideo(mediaFiles[currentImageIndex], currentImageIndex);

  // Video control functions
  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const togglePlay = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (videoRef.current) {
      if (isPlaying) {
        videoManager.pauseVideo(post.$id);
        setIsPlaying(false);
      } else {
        videoManager.playVideo(videoRef.current, post.$id);
        setIsPlaying(true);
      }
    }
  };

  // Intersection Observer for auto-play/pause
  useEffect(() => {
    if (!postRef.current || !isCurrentMediaVideo) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        
        if (entry.isIntersecting) {
          // Only auto-play if no other video is currently playing
          if (videoRef.current && !videoManager.isVideoPlaying(post.$id)) {
            setTimeout(() => {
              if (entry.isIntersecting && videoRef.current) {
                videoManager.playVideo(videoRef.current, post.$id);
                setIsPlaying(true);
              }
            }, 300); // Small delay to prevent rapid switching
          }
        } else {
          // Pause when out of view
          if (videoManager.isVideoPlaying(post.$id)) {
            videoManager.pauseVideo(post.$id);
            setIsPlaying(false);
          }
        }
      },
      { 
        threshold: 0.5,
        rootMargin: '-50px 0px'
      }
    );

    observer.observe(postRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isCurrentMediaVideo, post.$id, videoManager]);

  // Handle media changes
  useEffect(() => {
    if (isCurrentMediaVideo && videoRef.current) {
      setIsPlaying(false);
      videoRef.current.currentTime = 0;
    }
  }, [currentImageIndex, isCurrentMediaVideo]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      videoManager.pauseVideo(post.$id);
    };
  }, [post.$id, videoManager]);

  // Early returns after hooks
  if (!post.creatorId) {
    return null;
  }

  if (isCreatorLoading) {
    return (
      <div className="post-card animate-pulse">
        <div className="flex-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-dark-4 rounded-full"></div>
            <div className="flex flex-col gap-2">
              <div className="h-4 bg-dark-4 rounded w-32"></div>
              <div className="h-3 bg-dark-4 rounded w-24"></div>
            </div>
          </div>
        </div>
        <div className="h-4 bg-dark-4 rounded mb-2"></div>
        <div className="h-4 bg-dark-4 rounded w-3/4 mb-4"></div>
        <div className="h-64 bg-dark-4 rounded-xl"></div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % mediaFiles.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + mediaFiles.length) % mediaFiles.length);
  };

  return (
    <div className="post-card" ref={postRef}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
        <div className="flex min-w-0 items-center gap-3">
          <Link to={`/profile/${post.creatorId}`} className="shrink-0">
            <CreatorAvatar
              src={creatorImageUrl}
              name={creatorName}
              className="w-12 h-12 rounded-full object-cover"
            />
          </Link>

          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
              <Link to={`/profile/${post.creatorId}`} className="min-w-0 break-words base-medium lg:body-bold text-light-1">
                {creatorName}
                {creator?.isKYDone && (
                  <BadgeCheck className="inline ml-1 text-primary-500" size={16} />
                )}
              </Link>
               {showDealBadge && post.category && (
                <DealTypeBadge dealType={post.category} />
               )}
              {post.isSellingPost && <SellerRatingBadge creatorId={post.creatorId} />}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-light-3 sm:flex-nowrap">
              <p className="subtle-semibold lg:small-regular">{multiFormatDateString(post.$createdAt)}</p>
              {post.location && (
                <>
                  <span className="sm:mx-2">•</span>
                  <p className="subtle-semibold lg:small-regular">{post.location}</p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex self-end items-center gap-3 sm:self-auto sm:shrink-0">
          {/* Edit button - only show if authenticated and own post */}
          {isAuthenticated && post.isCommercialPost && user.id === post.creatorId && (
            <Link to={`/update-post/${post.$id}`}>
              <img src={"/assets/icons/edit.svg"} alt="edit" width={20} height={20} />
            </Link>
          )}

          {isAuthenticated && user.id !== post.creatorId && (
            <>
              <button
                type="button"
                onClick={() => setShowReportDialog(true)}
                className="flex items-center gap-1 text-xs text-light-3 hover:text-pink-400"
              >
                <Flag className="h-4 w-4" />
                Report
              </button>
              <button
                type="button"
                onClick={() => void handleBlockCreator()}
                disabled={creatorBlocked || isBlocking}
                className="flex items-center gap-1 text-xs text-pink-500 hover:text-pink-400 disabled:opacity-60"
              >
                <Ban className="h-4 w-4" />
                {creatorBlocked ? "Blocked" : isBlocking ? "Blocking..." : "Block"}
              </button>
            </>
          )}
        </div>
      </div>

      <Link
        to={`/posts/${post.$id}`}
        state={{
          creatorName,
          creatorImageUrl,
        }}>
        <div className="small-medium lg:base-medium py-5">
          <p className="whitespace-pre-wrap">{post.caption?.length > 250 ? `${post.caption.substring(0, 250)}...` : post.caption}</p>
          <ul className="flex flex-wrap gap-2 mt-2">
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {post.tags.slice(0, 4).map((tag: string, tagIndex: number) => (
                tag.trim() && (
                  <span
                    key={`${tag}-${tagIndex}`}
                    className="text-primary-400 text-xs px-2 py-1 bg-primary-500/10 rounded-md border border-primary-500/20 hover:bg-primary-500/20 transition-colors truncate max-w-[80px]"
                    title={`#${tag.trim()}`}
                  >
                    #{tag.trim().length > 8 ? `${tag.trim().substring(0, 8)}...` : tag.trim()}
                  </span>
                )
              ))}
              {post.tags.length > 4 && (
                <span className="text-light-3 text-xs px-2 py-1 bg-dark-4/50 rounded-md">
                  +{post.tags.length - 4}
                </span>
              )}
            </div>
          )}
          </ul>
        
        {/* Escrow Protection Badge */}
          {post.isCommercialPost !== false && (
            post.isSellingPost ? (
              <div className="relative">
                <div 
                  className="mt-3 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-md inline-flex items-center gap-2 hover:bg-emerald-100 transition-colors cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const tooltip = e.currentTarget.nextElementSibling;
                    tooltip?.classList.toggle('hidden');
                  }}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    className="h-5 w-5 bg-gradient-to-br from-emerald-400 to-blue-500 text-white p-0.5 rounded-full flex-shrink-0"
                    aria-hidden="true"
                  >
                    <path fill="currentColor" fillRule="evenodd" d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12c5.16-1.26 9-6.45 9-12V5l-9-4zm-1 17.93V12H5V6.3l6-2.67V18.93z" clipRule="evenodd"/>
                  </svg>

                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1">
                    <div className="flex items-center gap-1">
                      <span className="text-emerald-800 font-semibold text-sm">
                        Protected Payment
                      </span>
                    </div>
                    <span className="text-xs text-emerald-600">
                      (Recommended)
                    </span>
                  </div>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24"
                    className="h-4 w-4 text-emerald-500 ml-auto"
                    aria-hidden="true"
                  >
                    <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                  </svg>
                </div>

                <div 
                  className="absolute hidden z-50 w-full mt-1 p-3 text-sm bg-white border border-gray-200 rounded-lg shadow-lg"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-start mb-1">
                    <div className="font-medium text-gray-900">Secure Transaction</div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.closest('.absolute')?.classList.add('hidden');
                      }}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label="Close tooltip"
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                      </svg>
                    </button>
                  </div>
                  <ul className="space-y-1 text-gray-600">
                    <li className="flex items-start">
                      <svg className="h-4 w-4 text-emerald-500 mt-0.5 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      Funds held securely until delivery
                    </li>
                    <li className="flex items-start">
                      <svg className="h-4 w-4 text-emerald-500 mt-0.5 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      Fraud protection for both parties
                    </li>
                    <li className="flex items-start">
                      <svg className="h-4 w-4 text-emerald-500 mt-0.5 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      Faster payout upon confirmation
                    </li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="relative">
                <div 
                  className="mt-3 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md inline-flex items-center gap-2 hover:bg-amber-100 transition-colors cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const tooltip = e.currentTarget.nextElementSibling;
                    tooltip?.classList.toggle('hidden');
                  }}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    className="h-5 w-5 bg-gradient-to-br from-amber-400 to-orange-500 text-white p-0.5 rounded-full flex-shrink-0"
                    aria-hidden="true"
                  >
                    <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>

                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1">
                    <div className="flex items-center gap-1">
                      <span className="text-amber-800 font-semibold text-sm">
                        Direct Payment
                      </span>
                    </div>
                    <span className="text-xs text-amber-600">
                      (Caution advised)
                    </span>
                  </div>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24"
                    className="h-4 w-4 text-amber-500 ml-auto"
                    aria-hidden="true"
                  >
                    <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                  </svg>
                </div>

                <div 
                  className="absolute hidden z-50 w-full mt-1 p-3 text-sm bg-white border border-gray-200 rounded-lg shadow-lg"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-start mb-1">
                    <div className="font-medium text-gray-900">Direct Transaction</div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.closest('.absolute')?.classList.add('hidden');
                      }}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label="Close tooltip"
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                      </svg>
                    </button>
                  </div>
                  <ul className="space-y-1 text-gray-600">
                    <li className="flex items-start">
                      <svg className="h-4 w-4 text-amber-500 mt-0.5 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                      </svg>
                      No escrow protection
                    </li>
                    <li className="flex items-start">
                      <svg className="h-4 w-4 text-amber-500 mt-0.5 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                      </svg>
                      Higher risk of fraud
                    </li>
                    <li className="flex items-start">
                      <svg className="h-4 w-4 text-amber-500 mt-0.5 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                      </svg>
                      Proceed with caution
                    </li>
                  </ul>
                </div>
              </div>
            )
          )}
        </div>

        {/* Media Display */}
        {mediaFiles.length > 0 && (
          <div className="relative w-full rounded-xl overflow-hidden">
            {/* Navigation Arrows - Only show if multiple media */}
            {mediaFiles.length > 1 && (
              <>
                <button
                  onClick={(e) => { 
                    e.preventDefault(); 
                    e.stopPropagation();
                    prevImage(); 
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-sm p-2 rounded-full z-10 hover:bg-black/70 transition-colors"
                >
                  <ChevronLeft color="white" size={20} />
                </button>
                <button
                  onClick={(e) => { 
                    e.preventDefault(); 
                    e.stopPropagation();
                    nextImage(); 
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-sm p-2 rounded-full z-10 hover:bg-black/70 transition-colors"
                >
                  <ChevronRight color="white" size={20} />
                </button>
              </>
            )}

            {/* Image Counter */}
            {mediaFiles.length > 1 && (
              <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full z-10">
                <span className="text-white text-sm font-medium">
                  {currentImageIndex + 1} / {mediaFiles.length}
                </span>
              </div>
            )}

            {(() => {
              const currentMedia = mediaFiles[currentImageIndex];
              
              return isVideo(currentMedia, currentImageIndex) ? (
                <div className="relative w-full h-full">
                  <video
                    ref={videoRef}
                    src={currentMedia}
                    playsInline
                    loop
                    muted={isMuted}
                    preload="metadata"
                    disablePictureInPicture
                    controlsList="nodownload nofullscreen noremoteplaybook"
                    className="w-full h-auto max-h-[450px] object-contain rounded-xl"
                    poster="/assets/icons/video-placeholder.png"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      togglePlay();
                    }}
                    onError={(e) => console.error("Video error:", e)}
                  />
                  {/* Video Controls */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleMute(e);
                    }}
                    className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm p-3 rounded-full hover:bg-black/80 transition-all duration-200 z-10"
                  >
                    {isMuted ? (
                      <VolumeX color="white" size={20} />
                    ) : (
                      <Volume2 color="white" size={20} />
                    )}
                  </button>
                  {/* Play/Pause Overlay */}
                  {!isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-xl">
                      <div className="bg-white/90 rounded-full p-4">
                        <div className="w-0 h-0 border-l-[20px] border-l-black border-y-[15px] border-y-transparent ml-1"></div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <img
                  src={currentMedia || "/assets/icons/profile-placeholder.svg"}
                  alt={`Post media ${currentImageIndex + 1}`}
                  className="w-full h-auto max-h-[450px] object-contain rounded-xl"
                  loading="lazy"
                />
              );
            })()}
          </div>
        )}
      </Link>

      {/* Post Stats - Show differently for public users */}
      {isAuthenticated ? (
        <PostStats post={post} userId={user.id} postCreatorId={post.creatorId}/>
      ) : (
        <div className="flex-between mt-5">
          {/* Read-only stats for public users */}
          <div className="flex gap-6">
            <button
              type="button"
              onClick={redirectGuestToSignIn}
              className="flex items-center gap-2"
              aria-label="Sign in to like this post"
            >
              <img src="/assets/icons/like.svg" alt="heart" width={20} height={20} />
              <span className="small-regular text-light-3">
                {likesCount}
              </span>
            </button>
          </div>
          
          {/* Bookmark placeholder */}
          <button
            type="button"
            onClick={redirectGuestToSignIn}
            className="flex items-center gap-2"
            aria-label="Sign in to save this post"
          >
            <img src="/assets/icons/bookmark.svg" alt="bookmark" width={20} height={20} className="opacity-50" />
          </button>
        </div>
      )}

      {/* Public User Call-to-Action */}

      <ReportDialog
        open={showReportDialog}
        targetType="post"
        targetId={post.$id}
        onClose={() => setShowReportDialog(false)}
      />

    </div>
  );
};

export default PostCard;
